// Global
import React from 'react';

// Lib
import { UserProfile } from 'src/.generated/UserProfile/SignUp.model';
import SignUp from './SignUp';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useLinkWrapper } from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';
import styles from './SignUp.module.scss';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import useDictionary from 'lib/hooks/useDictionary';
import { Data } from '@generated/Foundation.GMI.model';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { useUserProfileUrls } from 'lib/hooks/useUserProfileUrls';
import { getQuestionOptions } from 'lib/utils/question-options';

type SignUpSitecoreProps = UserProfile.SignUp.SignUp_Component;

export const Default = (props: SignUpSitecoreProps): JSX.Element => {
  const siteSettings = useSiteSettings();
  const { getDictionaryValue } = useDictionary();
  const { manageAccountUrl } = useUserProfileUrls();
  const disabledFeatures = useDisabledFeatureFlags();
  const skipCaptcha = disabledFeatures.CAPTCHA !== false;
  const loginRegistrationSettings =
    siteSettings?.loginRegistrationSettings as Data.Settings.LoginAndRegistrationSettings;
  const sourceCode = (siteSettings as Record<string, { value?: string } | undefined> | null)
    ?.esrcCode?.value;
  const enableSocialLogin = loginRegistrationSettings?.fields?.enableSocialLogin?.value || false;
  const enableGoogleLogin =
    enableSocialLogin && !loginRegistrationSettings?.fields?.hideSocialLoginWithGoogle?.value;
  const enableFacebookLogin =
    enableSocialLogin && !loginRegistrationSettings?.fields?.hideSocialLoginWithFacebook?.value;
  const showBirthday = loginRegistrationSettings?.fields?.enableDOB?.value || false;
  const profileQuestionOptions = getQuestionOptions(props.fields?.questionsDropdownList || []);
  const showProfileQuestionDropdown =
    loginRegistrationSettings?.fields?.enableQuestionsDropdown?.value &&
    profileQuestionOptions.length > 0 &&
    !!props.fields?.questionId?.value;
  const isProfileQuestionRequired = props.fields?.isRequired?.value;
  const headline = usePlainTextWrapper({ field: props.fields?.headline });
  const description = useRichTextWrapper({
    field: props.fields?.description,
    className: styles.subHeadingDescription,
  });
  const signInLink = useLinkWrapper({
    field: props.fields?.loginLink,
    ctaStyle: 'link',
    ctaVariant: 'brand',
    className: styles.signInLink,
  });
  const emailLabel = usePlainTextWrapper({ field: props.fields?.emailLabel });
  const passwordLabel = usePlainTextWrapper({ field: props.fields?.passwordLabel });
  const birthdayLabel = usePlainTextWrapper({ field: props.fields?.birthdayLabel });
  const zipCodeLabel = usePlainTextWrapper({ field: props.fields?.zipCodeLabel });
  const newsLetterLabel = usePlainTextWrapper({ field: props.fields?.newsLetterLabel });
  const newsLetterText = usePlainTextWrapper({ field: props.fields?.newsLetterText });
  const legalText = useRichTextWrapper({ field: props.fields?.legalText });
  const signupButtonText = usePlainTextWrapper({ field: props.fields?.signupButtonText });

  const signupSidebarTitle = usePlainTextWrapper({ field: props.fields?.sidebarTitle });
  const signupSidebarDescription = useRichTextWrapper({
    field: props.fields?.sidebarDescription,
  });

  const alreadyMemberHeading = usePlainTextWrapper({ field: props.fields?.alreadyMemberHeading });
  const alreadyMemberDescription = useRichTextWrapper({
    field: props.fields?.alreadyMemberDescription,
  });
  const verifyEmailLabel = usePlainTextWrapper({ field: props.fields?.verifyEmailLabel });
  const socialConnectSubmitText = usePlainTextWrapper({
    field: props.fields?.socialConnectSubmitText,
  });
  const socialConnectCancelLink = useLinkWrapper({
    field: props.fields?.cancelCTA,
    ctaStyle: 'link',
    ctaVariant: 'brand',
  });
  const forgotPasswordLink = useLinkWrapper({
    field: props.fields?.forgotPasswordLink,
    ctaStyle: 'link',
    ctaVariant: 'brand',
    // className: existingUserStyles.forgotPasswordLink,
  });

  return (
    <SignUp
      skipCaptcha={skipCaptcha}
      headLine={headline}
      description={description}
      signInLink={signInLink}
      emailLabel={emailLabel}
      emailPlaceholderText={props.fields?.emailPlaceholderText?.value || ''}
      passwordLabel={passwordLabel}
      passwordPlaceholderText={props.fields?.passwordPlaceholderText?.value || ''}
      birthdayLabel={birthdayLabel}
      birthdayPlaceholderText={props.fields?.birthdayPlaceholderText?.value || ''}
      showBirthday={showBirthday}
      zipCodeLabel={zipCodeLabel}
      zipCodePlaceholderText={props.fields?.zipCodePlaceholderText?.value || ''}
      profileQuestionLabel={usePlainTextWrapper({ field: props.fields?.questionLabel })}
      profileQuestionPlaceholder={props.fields?.questionPlaceholder?.value || ''}
      profileQuestionOptions={profileQuestionOptions}
      showProfileQuestionDropdown={showProfileQuestionDropdown}
      profileQuestionRequired={isProfileQuestionRequired}
      profileQuestionId={props.fields?.questionId?.value || ''}
      newsLetterLabel={newsLetterLabel}
      newsLetterText={newsLetterText}
      newsletterSubscriptionId={siteSettings?.subscriptionID?.value || ''}
      signupButtonText={signupButtonText}
      legalText={legalText}
      signupSidebarTitle={signupSidebarTitle}
      signupSidebarDescription={signupSidebarDescription}
      enableGoogleLogin={enableGoogleLogin}
      enableFacebookLogin={enableFacebookLogin}
      successGateButton={
        <CtaButton
          ctaStyle="link"
          ctaVariant="brand"
          onClick={() => {
            if (manageAccountUrl) window.location.href = manageAccountUrl;
          }}
        >
          {getDictionaryValue('Components.Register.signupsuccessgatebutton') || 'Go to My Account'}
          <SvgIcon icon="chevron-right" size="sm" viewBox="0 0 14 22" />
        </CtaButton>
      }
      alreadyMemberHeading={alreadyMemberHeading}
      alreadyMemberDescription={alreadyMemberDescription}
      verifyEmailLabel={verifyEmailLabel}
      verifyEmailPlaceholder={props.fields?.verifyEmailPlaceholder?.value || ''}
      socialConnectSubmitText={socialConnectSubmitText}
      socialConnectCancelLink={socialConnectCancelLink}
      forgotPasswordLink={forgotPasswordLink}
      sourceCode={sourceCode}
    />
  );
};
